import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api, { AUTH_PAYLOAD_VERSION, clearAuthAndRedirect, saveAuthFromResponse } from '../../services/api';
import { toast } from 'react-toastify';
import {
  normalizeRoleKey,
  parseStoredList,
  roleMatchesAllowed,
} from '../../utils/roleCapabilities';
import ForbiddenScreen from './ForbiddenScreen';

function ProtectedRoute({ children, allowedRoles, allowedEmployeeCodes = [], requirePlatformAdmin = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState(normalizeRoleKey(localStorage.getItem('role')));
  const [actualRole, setActualRole] = useState(normalizeRoleKey(localStorage.getItem('actual_role')));
  const [employeeCode, setEmployeeCode] = useState(localStorage.getItem('employee_code'));
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(localStorage.getItem('is_platform_admin') === '1');
  const [permissions, setPermissions] = useState(parseStoredList('permissions'));

  useEffect(() => {
    let active = true;

    const checkAuthentication = async () => {
      const token = localStorage.getItem('access_token');
      const storedRole = localStorage.getItem('role');
      const storedActualRole = localStorage.getItem('actual_role');
      const storedEmployeeCode = localStorage.getItem('employee_code');
      const storedPlatformAdmin = localStorage.getItem('is_platform_admin') === '1';
      const storedPermissions = parseStoredList('permissions');

      if (!token) {
        if (!active) return;
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      const expiryRaw = localStorage.getItem('token_expiry');
      const expiryTime = Number(expiryRaw);
      const hasExpiry = expiryRaw !== null && Number.isFinite(expiryTime);
      const refreshToken = localStorage.getItem('refresh_token');
      const storedAuthPayloadVersion = localStorage.getItem('auth_payload_version');
      const needsAuthBackfill = Boolean(refreshToken)
        && (
          !storedRole
          || !storedEmployeeCode
          || localStorage.getItem('is_platform_admin') === null
          || storedAuthPayloadVersion !== AUTH_PAYLOAD_VERSION
        );

      if ((hasExpiry && Date.now() >= expiryTime) || needsAuthBackfill) {

        if (!refreshToken) {
          if (!active) return;
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        try {
          const response = await api.post(
            '/refresh',
            { refresh_token: refreshToken },
            { skipAuthRefresh: true }
          );

          saveAuthFromResponse(response.data);

          const refreshedRole =
            response.data?.effective_role ||
            response.data?.role ||
            storedRole ||
            localStorage.getItem('role');
          const refreshedActualRole =
            response.data?.user?.actual_role ||
            response.data?.actual_role ||
            storedActualRole ||
            localStorage.getItem('actual_role');
          const refreshedEmployeeCode =
            response.data?.user?.employee_code ||
            storedEmployeeCode ||
            localStorage.getItem('employee_code');
          const refreshedPlatformAdmin =
            typeof response.data?.is_platform_admin === 'boolean'
              ? response.data.is_platform_admin
              : (localStorage.getItem('is_platform_admin') === '1' || storedPlatformAdmin);
          const refreshedPermissions = Array.isArray(response.data?.permissions)
            ? response.data.permissions
            : parseStoredList('permissions');

          if (!active) return;
          setRole(normalizeRoleKey(refreshedRole));
          setActualRole(normalizeRoleKey(refreshedActualRole));
          setEmployeeCode(refreshedEmployeeCode ? String(refreshedEmployeeCode) : null);
          setIsPlatformAdmin(Boolean(refreshedPlatformAdmin));
          setPermissions(refreshedPermissions);
          setIsAuthenticated(true);
        } catch (err) {
          console.error('Token refresh failed:', err);
          toast.error('Session expired, please log in again.');
          clearAuthAndRedirect();
          if (!active) return;
          setIsAuthenticated(false);
        } finally {
          if (!active) return;
          setIsLoading(false);
        }

        return;
      }

      if (!active) return;
      setRole(normalizeRoleKey(storedRole));
      setActualRole(normalizeRoleKey(storedActualRole));
      setEmployeeCode(storedEmployeeCode || null);
      setIsPlatformAdmin(storedPlatformAdmin);
      setPermissions(storedPermissions);
      setIsAuthenticated(true);
      setIsLoading(false);
    };

    checkAuthentication();

    return () => {
      active = false;
    };
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requirePlatformAdmin && !isPlatformAdmin) {
    return (
      <ForbiddenScreen
        title="403 — Platform admin access required"
        message="This page is reserved for platform administrators."
        hint="If you need access for a specific task, ask the platform team."
      />
    );
  }

  const hasRoleRestriction = Array.isArray(allowedRoles) && allowedRoles.length > 0;
  const hasCodeRestriction = Array.isArray(allowedEmployeeCodes) && allowedEmployeeCodes.length > 0;
  const roleAllowed = roleMatchesAllowed({
    role,
    actualRole,
    permissions,
    allowedRoles: hasRoleRestriction ? allowedRoles : [],
    isPlatformAdmin,
  });
  const codeAllowed = hasCodeRestriction
    ? allowedEmployeeCodes.map((c) => String(c).trim()).includes(String(employeeCode || '').trim())
    : false;

  if (!roleAllowed && !codeAllowed) {
    return <ForbiddenScreen />;
  }

  return children;
}

export default ProtectedRoute;
